import PropTypes from "prop-types";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import CopyIcon from "./CopyIcon";
import CutIcon from "./CutIcon";
import "../styles.css";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const getUniqueElementsById = (elements = []) => {
  const seenIds = new Set();
  return elements.filter((element) => {
    if (!element || !element.id) return false;
    if (seenIds.has(element.id)) return false;
    seenIds.add(element.id);
    return true;
  });
};

const EDITOR_EDGE_BUFFER_PX = 10;
const MIN_X = EDITOR_EDGE_BUFFER_PX;
const MAX_X = window.innerWidth - 570 - EDITOR_EDGE_BUFFER_PX; // Same as RectangleEditor
const MIN_Y = EDITOR_EDGE_BUFFER_PX;
const MAX_Y = 760 - EDITOR_EDGE_BUFFER_PX; // Same as RectangleEditor

function SelectionEditor({
  selectedObjects = [],
  selectedObject = null,
  groupDragDisplay = null,
  setSelectedObjects,
  setSelectedObject,
  deleteSelectedObject,
  saveToHistory,
  onDuplicateToSelectedLocation,
  isMultiSelectMode = false,
  setIsMultiSelectMode = null,
  individuallySelectedIds = new Set(),
  setIndividuallySelectedIds = null,
  updateShapeProperties = null,
  updateArrowProperties = null,
  updateDoubleSidedArrowProperties = null,
  updateLineProperties = null,
  updateTextProperties = null,
  updateImageProperties = null,
  handleCopy = null,
  handleCut = null,
  handleCopyGroupBetweenCanvases = null, // Handler to copy/move entire group between canvases
  // Element arrays for showing all elements from same canvas
  rectangles = [],
  circles = [],
  images = [],
  texts = [],
  arrows = [],
  lines = [],
  doubleSidedArrows = [],
  offCanvasRectangles = [],
  offCanvasCircles = [],
  offCanvasImages = [],
  offCanvasTexts = [],
  offCanvasArrows = [],
  offCanvasLines = [],
  offCanvasDoubleSidedArrows = [],
  // Setter functions for deleting elements from arrays
  setRectangles = null,
  setCircles = null,
  setImages = null,
  setTexts = null,
  setArrows = null,
  setLines = null,
  setDoubleSidedArrows = null,
  setOffCanvasRectangles = null,
  setOffCanvasCircles = null,
  setOffCanvasImages = null,
  setOffCanvasTexts = null,
  setOffCanvasArrows = null,
  setOffCanvasLines = null,
  setOffCanvasDoubleSidedArrows = null,
  // Removed state setters for tracking deleted elements
  setRemovedRectangles = null,
  setRemovedCircles = null,
  setRemovedArrows = null,
  setRemovedDoubleSidedArrows = null,
  setRemovedScribbles = null,
  setRemovedTexts = null,
  setRemovedImages = null,
  setRemovedVideos = null,
  setRemovedLines = null,
  setRemovedOffCanvasRectangles = null,
  setRemovedOffCanvasCircles = null,
  setRemovedOffCanvasImages = null,
  setRemovedOffCanvasTexts = null,
  setRemovedOffCanvasArrows = null,
  setRemovedOffCanvasLines = null,
  setRemovedOffCanvasDoubleSidedArrows = null,
  // Clone mode props (like RectangleEditor)
  onToggleCloneMode = null,
  isCloneMode = false,
}) {
  // Local state for input values to avoid immediate updates (same as RectangleEditor)
  const [localInputValues, setLocalInputValues] = useState({
    x: "",
    y: "",
  });

  // State for collapsible sections
  const [selectedSectionCollapsed, setSelectedSectionCollapsed] =
    useState(false);
  const [unselectedSectionCollapsed, setUnselectedSectionCollapsed] =
    useState(false);

  // Custom scrollbar refs and state for Selected section
  const selectedScrollAreaRef = useRef(null);
  const selectedVTrackRef = useRef(null);
  const [selectedScrollMetrics, setSelectedScrollMetrics] = useState({
    scrollTop: 0,
    scrollHeight: 1,
    clientHeight: 1,
  });

  // Custom scrollbar refs and state for Not Selected section
  const unselectedScrollAreaRef = useRef(null);
  const unselectedVTrackRef = useRef(null);
  const [unselectedScrollMetrics, setUnselectedScrollMetrics] = useState({
    scrollTop: 0,
    scrollHeight: 1,
    clientHeight: 1,
  });

  // Drag state for both scrollbars
  const [dragState, setDragState] = useState(null);

  // Update scroll metrics for Selected section
  const updateSelectedScrollMetrics = useCallback(() => {
    const el = selectedScrollAreaRef.current;
    if (!el) return;

    const newMetrics = {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight || 1,
      clientHeight: el.clientHeight || 1,
    };

    setSelectedScrollMetrics(newMetrics);
  }, []);

  // Update scroll metrics for Not Selected section
  const updateUnselectedScrollMetrics = useCallback(() => {
    const el = unselectedScrollAreaRef.current;
    if (!el) return;

    const newMetrics = {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight || 1,
      clientHeight: el.clientHeight || 1,
    };

    setUnselectedScrollMetrics(newMetrics);
  }, []);

  // Throttled scroll updates
  const throttledUpdateSelectedScrollMetrics = useCallback(() => {
    requestAnimationFrame(updateSelectedScrollMetrics);
  }, [updateSelectedScrollMetrics]);

  const throttledUpdateUnselectedScrollMetrics = useCallback(() => {
    requestAnimationFrame(updateUnselectedScrollMetrics);
  }, [updateUnselectedScrollMetrics]);

  // Set up scroll listeners for Selected section
  useEffect(() => {
    const el = selectedScrollAreaRef.current;
    if (!el) return;
    updateSelectedScrollMetrics();
    const onScroll = () => throttledUpdateSelectedScrollMetrics();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateSelectedScrollMetrics);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateSelectedScrollMetrics);
    };
  }, [updateSelectedScrollMetrics, throttledUpdateSelectedScrollMetrics]);

  // Set up scroll listeners for Not Selected section
  useEffect(() => {
    const el = unselectedScrollAreaRef.current;
    if (!el) return;
    updateUnselectedScrollMetrics();
    const onScroll = () => throttledUpdateUnselectedScrollMetrics();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateUnselectedScrollMetrics);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateUnselectedScrollMetrics);
    };
  }, [updateUnselectedScrollMetrics, throttledUpdateUnselectedScrollMetrics]);

  // ResizeObserver for Selected section
  useEffect(() => {
    const el = selectedScrollAreaRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      updateSelectedScrollMetrics();
    });

    resizeObserver.observe(el);
    const firstChild = el.firstElementChild;
    if (firstChild) {
      resizeObserver.observe(firstChild);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateSelectedScrollMetrics]);

  // ResizeObserver for Not Selected section
  useEffect(() => {
    const el = unselectedScrollAreaRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      updateUnselectedScrollMetrics();
    });

    resizeObserver.observe(el);
    const firstChild = el.firstElementChild;
    if (firstChild) {
      resizeObserver.observe(firstChild);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateUnselectedScrollMetrics]);

  // Update scroll metrics when content changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateSelectedScrollMetrics();
      updateUnselectedScrollMetrics();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    selectedObjects,
    selectedObject,
    selectedSectionCollapsed,
    unselectedSectionCollapsed,
    updateSelectedScrollMetrics,
    updateUnselectedScrollMetrics,
  ]);

  // Scrollbar calculations for Selected section
  const selectedVTrackSize = selectedScrollAreaRef.current
    ? selectedScrollAreaRef.current.clientHeight
    : selectedScrollMetrics.clientHeight;

  const selectedVerticalVisibleRatio = Math.min(
    1,
    selectedScrollMetrics.clientHeight /
      Math.max(1, selectedScrollMetrics.scrollHeight)
  );

  const selectedVThumbHeight = useMemo(() => {
    const minSize = Math.max(16, Math.floor(selectedVTrackSize * 0.05));
    const calculatedSize = Math.floor(
      selectedVerticalVisibleRatio * selectedVTrackSize
    );
    return Math.max(minSize, calculatedSize);
  }, [selectedVerticalVisibleRatio, selectedVTrackSize]);

  const selectedVScrollable = Math.max(
    0,
    selectedScrollMetrics.scrollHeight - selectedScrollMetrics.clientHeight
  );

  const selectedVMaxThumbOffset = Math.max(
    0,
    selectedVTrackSize - selectedVThumbHeight
  );

  const selectedVThumbTop = useMemo(() => {
    if (selectedVScrollable <= 0 || selectedVMaxThumbOffset <= 0) return 0;
    const ratio = Math.max(
      0,
      Math.min(1, selectedScrollMetrics.scrollTop / selectedVScrollable)
    );
    return Math.round(ratio * selectedVMaxThumbOffset);
  }, [
    selectedScrollMetrics.scrollTop,
    selectedVScrollable,
    selectedVMaxThumbOffset,
  ]);

  // Scrollbar calculations for Not Selected section
  const unselectedVTrackSize = unselectedScrollAreaRef.current
    ? unselectedScrollAreaRef.current.clientHeight
    : unselectedScrollMetrics.clientHeight;

  const unselectedVerticalVisibleRatio = Math.min(
    1,
    unselectedScrollMetrics.clientHeight /
      Math.max(1, unselectedScrollMetrics.scrollHeight)
  );

  const unselectedVThumbHeight = useMemo(() => {
    const minSize = Math.max(16, Math.floor(unselectedVTrackSize * 0.05));
    const calculatedSize = Math.floor(
      unselectedVerticalVisibleRatio * unselectedVTrackSize
    );
    return Math.max(minSize, calculatedSize);
  }, [unselectedVerticalVisibleRatio, unselectedVTrackSize]);

  const unselectedVScrollable = Math.max(
    0,
    unselectedScrollMetrics.scrollHeight - unselectedScrollMetrics.clientHeight
  );

  const unselectedVMaxThumbOffset = Math.max(
    0,
    unselectedVTrackSize - unselectedVThumbHeight
  );

  const unselectedVThumbTop = useMemo(() => {
    if (unselectedVScrollable <= 0 || unselectedVMaxThumbOffset <= 0) return 0;
    const ratio = Math.max(
      0,
      Math.min(1, unselectedScrollMetrics.scrollTop / unselectedVScrollable)
    );
    return Math.round(ratio * unselectedVMaxThumbOffset);
  }, [
    unselectedScrollMetrics.scrollTop,
    unselectedVScrollable,
    unselectedVMaxThumbOffset,
  ]);

  // Drag handling for scrollbars
  const handleSelectedVerticalThumbMouseDown = (e) => {
    e.preventDefault();
    const el = selectedScrollAreaRef.current;
    if (!el) return;

    setDragState({
      section: "selected",
      axis: "v",
      startClient: e.clientY,
      startScroll: el.scrollTop,
    });
  };

  const handleSelectedVerticalTrackMouseDown = (e) => {
    // Don't handle if clicking on the thumb
    if (e.target !== selectedVTrackRef.current) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const el = selectedScrollAreaRef.current;
    if (!el) return;

    const rect = selectedVTrackRef.current.getBoundingClientRect();
    const contentScrollable = Math.max(0, el.scrollHeight - el.clientHeight);
    if (contentScrollable <= 0) return;

    const clickY = e.clientY - rect.top;
    const scrollRatio = Math.max(0, Math.min(1, clickY / rect.height));
    const newScrollTop = scrollRatio * contentScrollable;

    el.scrollTo({
      top: newScrollTop,
      behavior: "smooth",
    });

    // Update metrics after scroll
    setTimeout(() => updateSelectedScrollMetrics(), 100);
  };

  const handleUnselectedVerticalThumbMouseDown = (e) => {
    e.preventDefault();
    const el = unselectedScrollAreaRef.current;
    if (!el) return;

    setDragState({
      section: "unselected",
      axis: "v",
      startClient: e.clientY,
      startScroll: el.scrollTop,
    });
  };

  const handleUnselectedVerticalTrackMouseDown = (e) => {
    // Don't handle if clicking on the thumb
    if (e.target !== unselectedVTrackRef.current) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const el = unselectedScrollAreaRef.current;
    if (!el) return;

    const rect = unselectedVTrackRef.current.getBoundingClientRect();
    const contentScrollable = Math.max(0, el.scrollHeight - el.clientHeight);
    if (contentScrollable <= 0) return;

    const clickY = e.clientY - rect.top;
    const scrollRatio = Math.max(0, Math.min(1, clickY / rect.height));
    const newScrollTop = scrollRatio * contentScrollable;

    el.scrollTo({
      top: newScrollTop,
      behavior: "smooth",
    });

    // Update metrics after scroll
    setTimeout(() => updateUnselectedScrollMetrics(), 100);
  };

  // Global drag handler
  useEffect(() => {
    if (!dragState) {
      // Reset cursor when not dragging
      document.body.style.cursor = "";
      return;
    }

    // Set cursor to grabbing
    document.body.style.cursor = "grabbing";

    const onMove = (e) => {
      const el =
        dragState.section === "selected"
          ? selectedScrollAreaRef.current
          : unselectedScrollAreaRef.current;
      if (!el) return;

      if (dragState.axis === "v") {
        const delta = e.clientY - dragState.startClient;
        const contentScrollable = Math.max(
          0,
          el.scrollHeight - el.clientHeight
        );

        if (contentScrollable <= 0) return;

        // Calculate multiplier based on track size vs scrollable content
        const trackRef =
          dragState.section === "selected"
            ? selectedVTrackRef.current
            : unselectedVTrackRef.current;
        const trackHeight = trackRef ? trackRef.clientHeight : el.clientHeight;
        const thumbHeight =
          dragState.section === "selected"
            ? selectedVThumbHeight
            : unselectedVThumbHeight;
        const multiplier =
          contentScrollable / Math.max(1, trackHeight - thumbHeight);

        const newScrollTop = dragState.startScroll + delta * multiplier;
        const clampedScrollTop = Math.max(
          0,
          Math.min(contentScrollable, newScrollTop)
        );

        el.scrollTop = clampedScrollTop;

        // Update scroll metrics immediately
        if (dragState.section === "selected") {
          updateSelectedScrollMetrics();
        } else {
          updateUnselectedScrollMetrics();
        }
      }
    };

    const onUp = () => {
      document.body.style.cursor = "";
      setDragState(null);
    };

    document.addEventListener("mousemove", onMove, { passive: false });
    document.addEventListener("mouseup", onUp);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
  }, [
    dragState,
    selectedVThumbHeight,
    unselectedVThumbHeight,
    updateSelectedScrollMetrics,
    updateUnselectedScrollMetrics,
  ]);

  // Update local input values when selectedObjects, selectedObject, or groupDragDisplay change
  useEffect(() => {
    if (groupDragDisplay && groupDragDisplay.targetCanvas) {
      const x = Math.max(0, Math.round(groupDragDisplay.avgX));
      setLocalInputValues({
        x: x.toString(),
        y: Math.round(groupDragDisplay.avgY).toString(),
      });
      return;
    }
    const combined = selectedObjects ? [...selectedObjects] : [];
    if (
      selectedObject &&
      !combined.some((obj) => obj.id === selectedObject.id)
    ) {
      combined.push(selectedObject);
    }
    const uniqueCombined = getUniqueElementsById(combined);

    if (uniqueCombined.length > 0) {
      const avgX =
        uniqueCombined.reduce((sum, obj) => sum + (obj.x || 0), 0) /
        uniqueCombined.length;
      const avgY =
        uniqueCombined.reduce((sum, obj) => sum + (obj.y || 0), 0) /
        uniqueCombined.length;
      const x = Math.max(0, Math.round(avgX));
      setLocalInputValues({
        x: x.toString(),
        y: Math.round(avgY).toString(),
      });
    }
  }, [selectedObjects, selectedObject, groupDragDisplay]);

  // Helper function to check if an element is in main canvas arrays
  const isElementInMainCanvas = (elementId, elementType) => {
    if (!elementId || !elementType) return false;
    switch (elementType) {
      case "Rect":
        return rectangles.some((r) => r.id === elementId);
      case "Circle":
        return circles.some((c) => c.id === elementId);
      case "Image":
        return images.some((i) => i.id === elementId);
      case "Text":
        return texts.some((t) => t.id === elementId);
      case "Arrow":
        // Check both arrows array and lines array (for unified elements)
        // First check regular arrows array
        if (arrows.some((a) => a.id === elementId)) {
          return true;
        }
        // Then check lines array for unified arrow elements
        const lineInMain = lines.find((l) => l.id === elementId);
        if (lineInMain) {
          // If found in lines array, check if it's a unified arrow element
          // Check multiple possible property names for compatibility
          const hasStartArrow =
            lineInMain.hasStartArrow === true ||
            lineInMain.pointerAtBeginning === true ||
            lineInMain.pointerAtBeginning === "true";
          const hasEndArrow =
            lineInMain.hasEndArrow === true ||
            lineInMain.pointerAtEnd === true ||
            lineInMain.pointerAtEnding === true ||
            lineInMain.pointerAtEnd === "true" ||
            lineInMain.pointerAtEnding === "true";
          // If it has arrow properties, it's a unified arrow element
          if (hasStartArrow || hasEndArrow) {
            return true;
          }
          // If it's in lines array but doesn't have arrow properties, it's a regular line, not an arrow
          return false;
        }
        return false;
      case "Line":
        return lines.some((l) => {
          if (l.id !== elementId) return false;
          // Return true for any line element, including unified arrows/lines
          // Unified elements can have arrow flags set, but they're still in the lines array
          return true;
        });
      case "DoubleSidedArrow":
        // Check both doubleSidedArrows array and lines array (for unified elements)
        return (
          doubleSidedArrows.some((d) => d.id === elementId) ||
          lines.some((l) => {
            if (l.id !== elementId) return false;
            // Check if this line is a unified double-sided arrow element
            // Check multiple possible property names for compatibility
            const hasStartArrow =
              l.hasStartArrow === true ||
              l.pointerAtBeginning === true ||
              l.pointerAtBeginning === "true";
            const hasEndArrow =
              l.hasEndArrow === true ||
              l.pointerAtEnd === true ||
              l.pointerAtEnding === true ||
              l.pointerAtEnd === "true" ||
              l.pointerAtEnding === "true";
            return hasStartArrow && hasEndArrow;
          })
        );
      default:
        return false;
    }
  };

  // Helper function to check if an element is in off-canvas arrays
  const isElementInOffCanvas = (elementId, elementType) => {
    if (!elementId || !elementType) return false;
    switch (elementType) {
      case "Rect":
        return offCanvasRectangles.some((r) => r.id === elementId);
      case "Circle":
        return offCanvasCircles.some((c) => c.id === elementId);
      case "Image":
        return offCanvasImages.some((i) => i.id === elementId);
      case "Text":
        return offCanvasTexts.some((t) => t.id === elementId);
      case "Arrow":
        // Check both offCanvasArrows array and offCanvasLines array (for unified elements)
        // First check regular offCanvasArrows array
        if (offCanvasArrows.some((a) => a.id === elementId)) {
          return true;
        }
        // Then check offCanvasLines array for unified arrow elements
        const lineInOff = offCanvasLines.find((l) => l.id === elementId);
        if (lineInOff) {
          // If found in offCanvasLines array, check if it's a unified arrow element
          // Check multiple possible property names for compatibility
          const hasStartArrow =
            lineInOff.hasStartArrow === true ||
            lineInOff.pointerAtBeginning === true ||
            lineInOff.pointerAtBeginning === "true";
          const hasEndArrow =
            lineInOff.hasEndArrow === true ||
            lineInOff.pointerAtEnd === true ||
            lineInOff.pointerAtEnding === true ||
            lineInOff.pointerAtEnd === "true" ||
            lineInOff.pointerAtEnding === "true";
          // If it has arrow properties, it's a unified arrow element
          if (hasStartArrow || hasEndArrow) {
            return true;
          }
          // If it's in offCanvasLines array but doesn't have arrow properties, it's a regular line, not an arrow
          return false;
        }
        return false;
      case "Line":
        return offCanvasLines.some((l) => {
          if (l.id !== elementId) return false;
          // Return true for any line element, including unified arrows/lines
          // Unified elements can have arrow flags set, but they're still in the offCanvasLines array
          return true;
        });
      case "DoubleSidedArrow":
        // Check both offCanvasDoubleSidedArrows array and offCanvasLines array (for unified elements)
        return (
          offCanvasDoubleSidedArrows.some((d) => d.id === elementId) ||
          offCanvasLines.some((l) => {
            if (l.id !== elementId) return false;
            // Check if this line is a unified double-sided arrow element
            // Check multiple possible property names for compatibility
            const hasStartArrow =
              l.hasStartArrow === true ||
              l.pointerAtBeginning === true ||
              l.pointerAtBeginning === "true";
            const hasEndArrow =
              l.hasEndArrow === true ||
              l.pointerAtEnd === true ||
              l.pointerAtEnding === true ||
              l.pointerAtEnd === "true" ||
              l.pointerAtEnding === "true";
            return hasStartArrow && hasEndArrow;
          })
        );
      default:
        return false;
    }
  };

  // Determine which canvas type the selected elements are from
  const getSelectedElementsCanvasType = () => {
    // Build list of all selected elements (including selectedObject)
    const allSelectedElements = [];
    if (selectedObjects && selectedObjects.length > 0) {
      selectedObjects.forEach((obj) => {
        if (obj && obj.id && obj.type) {
          allSelectedElements.push(obj);
        }
      });
    }
    if (selectedObject && selectedObject.id && selectedObject.type) {
      const alreadyIncluded = allSelectedElements.some(
        (obj) => obj.id === selectedObject.id
      );
      if (!alreadyIncluded) {
        allSelectedElements.push(selectedObject);
      }
    }
    const uniqueSelectedElements = getUniqueElementsById(allSelectedElements);

    if (uniqueSelectedElements.length === 0) return null;

    // Check canvas type of all selected elements (by array membership when not dragging)
    const selectedElementsCanvasTypes = uniqueSelectedElements.map((obj) => {
      const isMain = isElementInMainCanvas(obj.id, obj.type);
      const isOff = isElementInOffCanvas(obj.id, obj.type);

      // Debug logging for unified elements
      if (
        obj.type === "Arrow" ||
        obj.type === "DoubleSidedArrow" ||
        obj.type === "Line"
      ) {
        const lineInMain = lines.find((l) => l.id === obj.id);
        const lineInOff = offCanvasLines.find((l) => l.id === obj.id);
        const arrowInMain = arrows.find((a) => a.id === obj.id);
        const arrowInOff = offCanvasArrows.find((a) => a.id === obj.id);
        const doubleArrowInMain = doubleSidedArrows.find(
          (d) => d.id === obj.id
        );
        const doubleArrowInOff = offCanvasDoubleSidedArrows.find(
          (d) => d.id === obj.id
        );

      }

      // Return "main" if in main canvas, "offCanvas" if in off-canvas, null if neither
      if (isMain) return "main";
      if (isOff) return "offCanvas";
      return null; // Element not found in either canvas (shouldn't happen)
    });

    // If any element is not found in either canvas, return null
    if (selectedElementsCanvasTypes.some((type) => type === null)) {

      // Fallback to drag target only when array membership can't be resolved.
      const targetDuringDrag = uniqueSelectedElements.find(
        (o) =>
          o._targetCanvasDuringDrag === "main" ||
          o._targetCanvasDuringDrag === "offCanvas" ||
          o._targetCanvasDuringDrag === "off-canvas"
      )?._targetCanvasDuringDrag;

      const normalizedTargetDuringDrag =
        targetDuringDrag === "off-canvas" ? "offCanvas" : targetDuringDrag;

      const dragTargetCanvas = groupDragDisplay?.targetCanvas;
      const normalizedDragTargetCanvas =
        dragTargetCanvas === "off-canvas" ? "offCanvas" : dragTargetCanvas;

      return (
        normalizedTargetDuringDrag ||
        normalizedDragTargetCanvas ||
        null
      );
    }

    // If all are from main canvas, return "main"
    if (selectedElementsCanvasTypes.every((type) => type === "main")) {
      return "main";
    }
    // If all are from off-canvas, return "offCanvas"
    if (selectedElementsCanvasTypes.every((type) => type === "offCanvas")) {
      return "offCanvas";
    }
    // Mixed types - shouldn't happen, but return null
    return null;
  };

  // Get all elements from the same canvas type as selected elements
  const getAllElementsFromSameCanvas = () => {
    const canvasType = getSelectedElementsCanvasType();
    if (!canvasType) return [];

    const allElements = [];

    if (canvasType === "main") {
      // Get all elements from main canvas
      rectangles.forEach((rect) => {
        allElements.push({ ...rect, type: "Rect" });
      });
      circles.forEach((circle) => {
        allElements.push({ ...circle, type: "Circle" });
      });
      images.forEach((img) => {
        allElements.push({ ...img, type: "Image" });
      });
      texts.forEach((text) => {
        allElements.push({ ...text, type: "Text" });
      });
      // First, collect all line IDs to avoid duplicates
      const lineIds = new Set(lines.map((l) => l.id));

      arrows.forEach((arrow) => {
        // Only include legacy arrows (not in lines array)
        // Unified elements are stored in lines array, so skip if already in lines
        if (!lineIds.has(arrow.id)) {
          allElements.push({ ...arrow, type: "Arrow" });
        }
      });
      doubleSidedArrows.forEach((arrow) => {
        // Only include legacy double-sided arrows (not in lines array)
        // Unified elements are stored in lines array, so skip if already in lines
        if (!lineIds.has(arrow.id)) {
          allElements.push({ ...arrow, type: "DoubleSidedArrow" });
        }
      });
      lines.forEach((line) => {
        // Check if this is a unified element
        const hasStartArrow =
          line.hasStartArrow === true || line.pointerAtBeginning === true;
        const hasEndArrow =
          line.hasEndArrow === true || line.pointerAtEnd === true;

        let elementType = "Line";
        if (hasStartArrow && hasEndArrow) {
          elementType = "DoubleSidedArrow";
        } else if (hasStartArrow || hasEndArrow) {
          elementType = "Arrow";
        }

        allElements.push({ ...line, type: elementType });
      });
    } else {
      // Get all elements from off-canvas
      offCanvasRectangles.forEach((rect) => {
        allElements.push({ ...rect, type: "Rect" });
      });
      offCanvasCircles.forEach((circle) => {
        allElements.push({ ...circle, type: "Circle" });
      });
      offCanvasImages.forEach((img) => {
        allElements.push({ ...img, type: "Image" });
      });
      offCanvasTexts.forEach((text) => {
        allElements.push({ ...text, type: "Text" });
      });
      // First, collect all off-canvas line IDs to avoid duplicates
      const offCanvasLineIds = new Set(offCanvasLines.map((l) => l.id));

      offCanvasArrows.forEach((arrow) => {
        // Only include legacy arrows (not in offCanvasLines array)
        // Unified elements are stored in offCanvasLines array, so skip if already in lines
        if (!offCanvasLineIds.has(arrow.id)) {
          allElements.push({ ...arrow, type: "Arrow" });
        }
      });
      offCanvasDoubleSidedArrows.forEach((arrow) => {
        // Only include legacy double-sided arrows (not in offCanvasLines array)
        // Unified elements are stored in offCanvasLines array, so skip if already in lines
        if (!offCanvasLineIds.has(arrow.id)) {
          allElements.push({ ...arrow, type: "DoubleSidedArrow" });
        }
      });
      offCanvasLines.forEach((line) => {
        // Check if this is a unified element
        const hasStartArrow =
          line.hasStartArrow === true || line.pointerAtBeginning === true;
        const hasEndArrow =
          line.hasEndArrow === true || line.pointerAtEnd === true;

        let elementType = "Line";
        if (hasStartArrow && hasEndArrow) {
          elementType = "DoubleSidedArrow";
        } else if (hasStartArrow || hasEndArrow) {
          elementType = "Arrow";
        }

        allElements.push({ ...line, type: elementType });
      });
    }

    return getUniqueElementsById(allElements);
  };

  const handleEnterKey = (event, commitFn) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitFn();
    }
  };

  const commitPositionValue = (field, rawValue, min, max) => {
    let num = parseInt(rawValue, 10);
    if (rawValue === "" || Number.isNaN(num)) num = min;
    if (num < min) num = min;
    if (num > max) num = max;

    setLocalInputValues((prev) => ({
      ...prev,
      [field]: num.toString(),
    }));

    // Get selectedObjects array - this is what we'll update
    const currentSelectedObjects = selectedObjects || [];
    if (currentSelectedObjects.length === 0) return;

    // Calculate current average position
    const currentAvgX =
      currentSelectedObjects.reduce((sum, obj) => sum + (obj.x || 0), 0) /
      currentSelectedObjects.length;
    const currentAvgY =
      currentSelectedObjects.reduce((sum, obj) => sum + (obj.y || 0), 0) /
      currentSelectedObjects.length;

    // Calculate offset
    const offsetX = field === "x" ? num - currentAvgX : 0;
    const offsetY = field === "y" ? num - currentAvgY : 0;

    // Simply update selectedObjects array with new positions
    const updated = currentSelectedObjects.map((obj) => {
      const newX = field === "x" ? (obj.x || 0) + offsetX : obj.x || 0;
      const newY = field === "y" ? (obj.y || 0) + offsetY : obj.y || 0;

      return {
        ...obj,
        x: newX,
        y: newY,
      };
    });

    setSelectedObjects(updated);

    // Directly update element arrays - simple and direct
    updated.forEach((obj) => {
      const newX = field === "x" ? (obj.x || 0) + offsetX : obj.x || 0;
      const newY = field === "y" ? (obj.y || 0) + offsetY : obj.y || 0;

      // Check which canvas the element is on and update accordingly
      const isMainCanvas = isElementInMainCanvas(obj.id, obj.type);

      switch (obj.type) {
        case "Rect":
          if (isMainCanvas && setRectangles) {
            setRectangles((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          } else if (!isMainCanvas && setOffCanvasRectangles) {
            setOffCanvasRectangles((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          }
          break;
        case "Circle":
          if (isMainCanvas && setCircles) {
            setCircles((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          } else if (!isMainCanvas && setOffCanvasCircles) {
            setOffCanvasCircles((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          }
          break;
        case "Image":
          if (isMainCanvas && setImages) {
            setImages((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          } else if (!isMainCanvas && setOffCanvasImages) {
            setOffCanvasImages((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          }
          break;
        case "Text":
          if (isMainCanvas && setTexts) {
            setTexts((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          } else if (!isMainCanvas && setOffCanvasTexts) {
            setOffCanvasTexts((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          }
          break;
        case "Arrow":
          // Check if unified arrow (in lines array)
          const arrowInLines = isMainCanvas
            ? lines.find(
                (l) =>
                  l.id === obj.id &&
                  (l.hasStartArrow ||
                    l.hasEndArrow ||
                    l.pointerAtBeginning ||
                    l.pointerAtEnd)
              )
            : offCanvasLines.find(
                (l) =>
                  l.id === obj.id &&
                  (l.hasStartArrow ||
                    l.hasEndArrow ||
                    l.pointerAtBeginning ||
                    l.pointerAtEnd)
              );

          if (arrowInLines) {
            // Unified arrow - update lines array
            if (isMainCanvas && setLines) {
              setLines((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            } else if (!isMainCanvas && setOffCanvasLines) {
              setOffCanvasLines((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            }
          } else {
            // Legacy arrow - update arrows array
            if (isMainCanvas && setArrows) {
              setArrows((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            } else if (!isMainCanvas && setOffCanvasArrows) {
              setOffCanvasArrows((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            }
          }
          break;
        case "DoubleSidedArrow":
          // Check if unified double-sided arrow (in lines array)
          const doubleArrowInLines = isMainCanvas
            ? lines.find(
                (l) => l.id === obj.id && l.hasStartArrow && l.hasEndArrow
              )
            : offCanvasLines.find(
                (l) => l.id === obj.id && l.hasStartArrow && l.hasEndArrow
              );

          if (doubleArrowInLines) {
            // Unified double-sided arrow - update lines array
            if (isMainCanvas && setLines) {
              setLines((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            } else if (!isMainCanvas && setOffCanvasLines) {
              setOffCanvasLines((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            }
          } else {
            // Legacy double-sided arrow - update doubleSidedArrows array
            if (isMainCanvas && setDoubleSidedArrows) {
              setDoubleSidedArrows((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            } else if (!isMainCanvas && setOffCanvasDoubleSidedArrows) {
              setOffCanvasDoubleSidedArrows((prev) =>
                prev.map((el) =>
                  el.id === obj.id ? { ...el, x: newX, y: newY } : el
                )
              );
            }
          }
          break;
        case "Line":
          if (isMainCanvas && setLines) {
            setLines((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          } else if (!isMainCanvas && setOffCanvasLines) {
            setOffCanvasLines((prev) =>
              prev.map((el) =>
                el.id === obj.id ? { ...el, x: newX, y: newY } : el
              )
            );
          }
          break;
      }
    });

    // Update selectedObject if it exists
    if (selectedObject) {
      const updatedSelected = updated.find((o) => o.id === selectedObject.id);
      if (updatedSelected) {
        setSelectedObject(updatedSelected);
      }
    }

    // Save to history
    if (saveToHistory) {
      saveToHistory();
    }
  };

  const handleCancel = () => {
    if (setSelectedObjects) {
      setSelectedObjects([]);
    }
    if (setSelectedObject) {
      setSelectedObject(null);
    }
    // Exit multi-select mode to re-enable toolbar options
    if (setIsMultiSelectMode) {
      setIsMultiSelectMode(false);
    }
    if (setIndividuallySelectedIds) {
      setIndividuallySelectedIds(new Set());
    }
  };

  const handleDelete = () => {
    // Get all selected elements to delete
    const elementsToDelete = effectiveSelectedObjects;

    if (elementsToDelete.length === 0) return;

    // CRITICAL: Save state BEFORE deletion so one undo restores all deleted elements.
    // This path runs when user clicks the Delete button in the selection panel (or when
    // Delete key is handled by the panel). Without this, no history entry is pushed.
    if (saveToHistory) {
      saveToHistory(
        "delete_multiple_objects",
        images,
        arrows,
        doubleSidedArrows,
        lines,
        texts,
        null, // currentSelectedTextOverride
        null, // currentSelectedTextIdOverride
        rectangles,
        null, // currentOffCanvasRectanglesOverride
        circles,
        null, // currentOffCanvasCirclesOverride
        null, // currentOffCanvasArrowsOverride
        null, // currentOffCanvasDoubleSidedArrowsOverride
        null, // currentOffCanvasLinesOverride
        null, // currentOffCanvasTextsOverride
        null, // currentOffCanvasImagesOverride
        elementsToDelete.map((obj) => obj.id).join(","),
        elementsToDelete.map((obj) => obj.type).join(",")
      );
    }

    // CRITICAL: Delete each element and properly track removed IDs
    // This replicates the logic from deletionUtils.js but handles multiple elements
    elementsToDelete.forEach((obj) => {
      if (!obj || !obj.id || !obj.type) return;

      const isMainCanvas = isElementInMainCanvas(obj.id, obj.type);

      // Delete from the appropriate array and track removed IDs
      switch (obj.type) {
        case "Rect":
          if (isMainCanvas && setRectangles) {
            setRectangles((prev) => {
              const filtered = prev.filter((r) => r.id !== obj.id);
              if (filtered.length < prev.length && setRemovedRectangles) {
                setRemovedRectangles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasRectangles) {
            setOffCanvasRectangles((prev) => {
              const filtered = prev.filter((r) => r.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasRectangles) {
                setRemovedOffCanvasRectangles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Circle":
          if (isMainCanvas && setCircles) {
            setCircles((prev) => {
              const filtered = prev.filter((c) => c.id !== obj.id);
              if (filtered.length < prev.length && setRemovedCircles) {
                setRemovedCircles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasCircles) {
            setOffCanvasCircles((prev) => {
              const filtered = prev.filter((c) => c.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasCircles) {
                setRemovedOffCanvasCircles((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Image":
          if (isMainCanvas && setImages) {
            setImages((prev) => {
              const filtered = prev.filter((i) => i.id !== obj.id);
              if (filtered.length < prev.length && setRemovedImages) {
                setRemovedImages((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasImages) {
            setOffCanvasImages((prev) => {
              const filtered = prev.filter((i) => i.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasImages) {
                setRemovedOffCanvasImages((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Text":
          if (isMainCanvas && setTexts) {
            setTexts((prev) => {
              const filtered = prev.filter((t) => t.id !== obj.id);
              if (filtered.length < prev.length && setRemovedTexts) {
                setRemovedTexts((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasTexts) {
            setOffCanvasTexts((prev) => {
              const filtered = prev.filter((t) => t.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasTexts) {
                setRemovedOffCanvasTexts((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "Arrow":
          // Check if this is a unified arrow element (stored in lines array)
          const arrowInLines = isMainCanvas
            ? lines.find((l) => l.id === obj.id)
            : offCanvasLines.find((l) => l.id === obj.id);

          if (arrowInLines) {
            // Unified arrow element - delete from lines array
            if (isMainCanvas && setLines) {
              setLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedLines) {
                  setRemovedLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasLines) {
              setOffCanvasLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedOffCanvasLines) {
                  setRemovedOffCanvasLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          } else {
            // Legacy arrow element - delete from arrows array
            if (isMainCanvas && setArrows) {
              setArrows((prev) => {
                const filtered = prev.filter((a) => a.id !== obj.id);
                if (filtered.length < prev.length && setRemovedArrows) {
                  setRemovedArrows((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasArrows) {
              setOffCanvasArrows((prev) => {
                const filtered = prev.filter((a) => a.id !== obj.id);
                if (filtered.length < prev.length && setRemovedOffCanvasArrows) {
                  setRemovedOffCanvasArrows((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          }
          break;
        case "Line":
          if (isMainCanvas && setLines) {
            setLines((prev) => {
              const filtered = prev.filter((l) => l.id !== obj.id);
              if (filtered.length < prev.length && setRemovedLines) {
                setRemovedLines((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          } else if (!isMainCanvas && setOffCanvasLines) {
            setOffCanvasLines((prev) => {
              const filtered = prev.filter((l) => l.id !== obj.id);
              if (filtered.length < prev.length && setRemovedOffCanvasLines) {
                setRemovedOffCanvasLines((removed) => [...removed, obj.id]);
              }
              return filtered;
            });
          }
          break;
        case "DoubleSidedArrow":
          // Check if this is a unified double-sided arrow element (stored in lines array)
          const doubleArrowInLines = isMainCanvas
            ? lines.find((l) => l.id === obj.id)
            : offCanvasLines.find((l) => l.id === obj.id);

          if (doubleArrowInLines) {
            // Unified double-sided arrow element - delete from lines array
            if (isMainCanvas && setLines) {
              setLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedLines) {
                  setRemovedLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasLines) {
              setOffCanvasLines((prev) => {
                const filtered = prev.filter((l) => l.id !== obj.id);
                if (filtered.length < prev.length && setRemovedOffCanvasLines) {
                  setRemovedOffCanvasLines((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          } else {
            // Legacy double-sided arrow element - delete from doubleSidedArrows array
            if (isMainCanvas && setDoubleSidedArrows) {
              setDoubleSidedArrows((prev) => {
                const filtered = prev.filter((d) => d.id !== obj.id);
                if (filtered.length < prev.length && setRemovedDoubleSidedArrows) {
                  setRemovedDoubleSidedArrows((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            } else if (!isMainCanvas && setOffCanvasDoubleSidedArrows) {
              setOffCanvasDoubleSidedArrows((prev) => {
                const filtered = prev.filter((d) => d.id !== obj.id);
                if (filtered.length < prev.length && setRemovedOffCanvasDoubleSidedArrows) {
                  setRemovedOffCanvasDoubleSidedArrows((removed) => [...removed, obj.id]);
                }
                return filtered;
              });
            }
          }
          break;
        default:
      }
    });

    // Clear selection
    if (setSelectedObjects) {
      setSelectedObjects([]);
    }
    if (setSelectedObject) {
      setSelectedObject(null);
    }
    
    // Exit multi-select mode after deletion
    if (setIsMultiSelectMode) {
      setIsMultiSelectMode(false);
    }
    if (setIndividuallySelectedIds) {
      setIndividuallySelectedIds(new Set());
    }
  };

  const getElementDisplayName = (obj, index) => {
    // For unified line/arrow elements (Arrow, DoubleSidedArrow, Line), always show "Stroke"
    if (
      obj.type === "Arrow" ||
      obj.type === "DoubleSidedArrow" ||
      obj.type === "Line"
    ) {
      // Extract the number from the name if it exists, otherwise use index
      if (obj.name) {
        const match = obj.name.match(/(\d+)$/);
        const number = match ? match[1] : index + 1;
        return `Stroke ${number}`;
      }
      return `Stroke ${index + 1}`;
    }

    // For other element types, use the name if available
    if (obj.name) return obj.name;

    // Default names for other types
    if (obj.type === "Image") return `Picture ${index + 1}`;
    if (obj.type === "Circle") return "Circle";
    if (obj.type === "Rect") return `Rectangle ${index + 1}`;
    if (obj.type === "Text") return `Text ${index + 1}`;
    return `${obj.type} ${index + 1}`;
  };

  // Handler to add an element to the selection
  const handleAddElementToSelection = (element) => {
    if (!setSelectedObjects || !element) return;

    // Check if element is already selected (check both selectedObjects and selectedObject)
    const currentSelectedObjects = selectedObjects || [];
    const isAlreadySelected =
      currentSelectedObjects.some((obj) => obj.id === element.id) ||
      (selectedObject && selectedObject.id === element.id);
    if (isAlreadySelected) return;

    // Build updated array: first ensure selectedObject is included (if it exists and isn't already in array)
    let updatedSelectedObjects = [...currentSelectedObjects];

    // If selectedObject exists and is not in selectedObjects, add it first
    if (
      selectedObject &&
      !updatedSelectedObjects.some((obj) => obj.id === selectedObject.id)
    ) {
      updatedSelectedObjects.push(selectedObject);
    }

    // Then add the new element and keep unique IDs only
    updatedSelectedObjects = getUniqueElementsById([
      ...updatedSelectedObjects,
      element,
    ]);

    setSelectedObjects(updatedSelectedObjects);

    // Add to individuallySelectedIds if in multi-select mode
    if (
      (isMultiSelectMode || individuallySelectedIds.size > 0) &&
      setIndividuallySelectedIds
    ) {
      const newSet = new Set(individuallySelectedIds);
      // Add selectedObject's ID if it was added
      if (
        selectedObject &&
        !currentSelectedObjects.some((obj) => obj.id === selectedObject.id)
      ) {
        newSet.add(selectedObject.id);
      }
      // Add the new element's ID
      newSet.add(element.id);
      setIndividuallySelectedIds(newSet);
    }
  };

  // Get all elements from same canvas, separated into selected and unselected
  const getAllElementsFromSameCanvasSeparated = () => {
    const allElements = getAllElementsFromSameCanvas();
    const selectedIds = new Set();

    // Build set of selected IDs (from selectedObjects and selectedObject)
    if (selectedObjects && selectedObjects.length > 0) {
      selectedObjects.forEach((obj) => {
        if (obj && obj.id) {
          selectedIds.add(obj.id);
        }
      });
    }
    if (selectedObject && selectedObject.id) {
      selectedIds.add(selectedObject.id);
    }

    const selected = [];
    const unselected = [];

    allElements.forEach((element) => {
      if (selectedIds.has(element.id)) {
        selected.push(element);
      } else {
        unselected.push(element);
      }
    });

    return { selected, unselected };
  };

  // Create a combined array that includes both selectedObjects and selectedObject
  // This ensures we show the selection even when only selectedObject is set
  // This hook must be called unconditionally (before any early returns)
  const effectiveSelectedObjects = useMemo(() => {
    const combined = selectedObjects ? [...selectedObjects] : [];
    if (
      selectedObject &&
      !combined.some((obj) => obj.id === selectedObject.id)
    ) {
      combined.push(selectedObject);
    }
    return getUniqueElementsById(combined);
  }, [selectedObjects, selectedObject]);

  // Show SelectionEditor if:
  // 1. There are selectedObjects, OR
  // 2. Multi-select mode is enabled and there's a selectedObject
  const hasSelectedObjects = selectedObjects && selectedObjects.length > 0;
  const hasSelectedObject =
    selectedObject !== null && selectedObject !== undefined;
  const shouldShow =
    hasSelectedObjects || (isMultiSelectMode && hasSelectedObject);

  if (!shouldShow) {
    return null;
  }

  return (
    <div
      className="selection-editor-root"
      style={{
        pointerEvents: "auto",
        minHeight: "100%",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Position - Same UI as RectangleEditor */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
          display: "flex",
          gap: "16px",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <label style={{ whiteSpace: "nowrap" }}>XPos</label>
          <input
            type="number"
            value={localInputValues.x}
            min={MIN_X}
            max={MAX_X}
            onChange={(e) => {
              const { value } = e.target;
              const inputType = e.nativeEvent?.inputType;
              setLocalInputValues((prev) => ({
                ...prev,
                x: value,
              }));
              if (value === "" || shouldDeferInputUpdate(inputType)) {
                return;
              }
              commitPositionValue("x", value, MIN_X, MAX_X);
            }}
            onBlur={(e) =>
              commitPositionValue("x", e.target.value, MIN_X, MAX_X)
            }
            onKeyDown={(e) =>
              handleEnterKey(e, () =>
                commitPositionValue("x", e.target.value, MIN_X, MAX_X)
              )
            }
            style={{
              width: "60px",
              padding: "4px 8px",
              marginLeft: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
            }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <label style={{ whiteSpace: "nowrap" }}>YPos</label>
          <input
            type="number"
            value={localInputValues.y}
            min={MIN_Y}
            max={MAX_Y}
            onChange={(e) => {
              const { value } = e.target;
              const inputType = e.nativeEvent?.inputType;
              setLocalInputValues((prev) => ({
                ...prev,
                y: value,
              }));
              if (value === "" || shouldDeferInputUpdate(inputType)) {
                return;
              }
              commitPositionValue("y", value, MIN_Y, MAX_Y);
            }}
            onBlur={(e) =>
              commitPositionValue("y", e.target.value, MIN_Y, MAX_Y)
            }
            onKeyDown={(e) =>
              handleEnterKey(e, () =>
                commitPositionValue("y", e.target.value, MIN_Y, MAX_Y)
              )
            }
            style={{
              width: "60px",
              padding: "4px 8px",
              marginLeft: "17px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
            }}
          />
        </div>
      </div>

      {/* Copy and Cut buttons - Commented out */}
      {false && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            marginBottom: "20px",
          }}
        >
          {/* Copy button commented out */}
          {handleCopy && (
            <div
              onClick={handleCopy}
              style={{
                cursor: "pointer",
                borderRadius: "4px",
                background: "#F5F6F8",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "270px",
                height: "40px",
                opacity: 1,
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#E5E7EB")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#F5F6F8")}
              title="Copy"
            >
              <CopyIcon width={16} height={19} fill="#757575" />
            </div>
          )}
          {handleCut && (
            <div
              onClick={handleCut}
              style={{
                cursor: "pointer",
                borderRadius: "4px",
                background: "#F5F6F8",
                transition: "background 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "270px",
                height: "40px",
                opacity: 1,
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "#E5E7EB")
              }
              onMouseOut={(e) => (e.currentTarget.style.background = "#F5F6F8")}
              title="Cut"
            >
              <CutIcon width={20} height={20} fill="#757575" />
            </div>
          )}
        </div>
      )}

      {/* Duplicate to Selected Location button */}
      {onToggleCloneMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Use setTimeout to ensure the toggle happens after event propagation is stopped
            setTimeout(() => {
              // Pass effectiveSelectedObjects for multi-clone support
              onToggleCloneMode(effectiveSelectedObjects);
            }, 0);
          }}
          style={{
            width: "100%",
            padding: "10px 20px",
            backgroundColor: isCloneMode ? "#3B82F6" : "#F3F4F6",
            color: isCloneMode ? "white" : "#374151",
            border: isCloneMode ? "1px solid #3B82F6" : "1px solid #D1D5DB",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            marginBottom: "12px",
          }}
        >
          <CopyIcon
            width={16}
            height={19}
            fill={isCloneMode ? "white" : "#374151"}
          />
          <span>Duplicate to Selected Location</span>
        </button>
      )}

      {/* List of selected elements and unselected elements from same canvas */}
      <div style={{ marginBottom: "20px" }}>
        {/* Selected Elements Section */}
        <div style={{ marginBottom: "12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
              cursor: "pointer",
            }}
            onClick={() => setSelectedSectionCollapsed((prev) => !prev)}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "14px",
                color: "#374151",
              }}
            >
              Selected
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSectionCollapsed((prev) => !prev);
              }}
              aria-label={
                selectedSectionCollapsed
                  ? "Expand selected elements"
                  : "Collapse selected elements"
              }
              style={{
                background: "transparent",
                border: "none",
                padding: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <svg
                width="14"
                height="8"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transition: "transform 0.2s ease",
                  transform: selectedSectionCollapsed
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transformOrigin: "center",
                }}
              >
                <path
                  d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                  fill="#6C757D"
                />
              </svg>
            </button>
          </div>
          {!selectedSectionCollapsed && effectiveSelectedObjects.length > 0 && (
            <div
              style={{
                position: "relative",
                height: selectedVScrollable > 0 ? "300px" : "auto",
                maxHeight: "300px",
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                overflow: "hidden",
              }}
            >
              <div
                ref={selectedScrollAreaRef}
                className="selection-scroll-container"
                style={{
                  height: "100%",
                  maxHeight: "300px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  padding: "8px 8px 8px 8px",
                }}
              >
                <div
                  style={{
                    paddingBottom: selectedVScrollable > 0 ? "12px" : "0px",
                  }}
                >
                  {/* Selected elements */}
                  {effectiveSelectedObjects.map((obj, index) => {
                    const isPrimary = selectedObject?.id === obj.id; // Check if this is the currently selected object
                    const isIndividuallySelected = individuallySelectedIds.has(
                      obj.id
                    );
                    // Since we're iterating over effectiveSelectedObjects, all elements here are selected
                    // Show blue background for all selected elements
                    const showAsSelected = true;

                    // Show checkbox when individuallySelectedIds has items (selection box case) or multi-select mode is enabled
                    const showCheckbox =
                      individuallySelectedIds.size > 0 || isMultiSelectMode;

                    // Handler to remove element from selection
                    const handleRemoveFromSelection = (e) => {
                      e.stopPropagation(); // Prevent event bubbling

                      const isCurrentlySelectedObject =
                        selectedObject?.id === obj.id;
                      const currentSelectedObjects = selectedObjects || [];
                      const updatedSelectedObjects =
                        currentSelectedObjects.filter(
                          (selectedObj) => selectedObj.id !== obj.id
                        );

                      setSelectedObjects(updatedSelectedObjects);

                      // If this was the selectedObject, set it to another element or null
                      if (isCurrentlySelectedObject) {
                        if (updatedSelectedObjects.length > 0) {
                          // Set to the first remaining element
                          setSelectedObject(updatedSelectedObjects[0]);
                        } else {
                          // No more selected objects, clear selectedObject
                          setSelectedObject(null);
                        }
                      }

                      // Also remove from individuallySelectedIds if present
                      if (
                        setIndividuallySelectedIds &&
                        individuallySelectedIds.has(obj.id)
                      ) {
                        const newSet = new Set(individuallySelectedIds);
                        newSet.delete(obj.id);
                        setIndividuallySelectedIds(newSet);
                      }
                    };

                    return (
                      <div
                        key={obj.id}
                        style={{
                          padding: "8px",
                          marginBottom: "4px",
                          backgroundColor: showAsSelected
                            ? "#CAE2FF"
                            : "transparent",
                          border: "none",
                          borderRadius: "4px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            color: showAsSelected ? "#0D6EFD" : "inherit",
                            flex: 1,
                          }}
                        >
                          {getElementDisplayName(obj, index + 1)}
                        </span>
                        {/* Delete button - at the end */}
                        <div
                          onClick={handleRemoveFromSelection}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            flexShrink: 0,
                            padding: "2px",
                          }}
                          aria-label={`Remove ${getElementDisplayName(
                            obj,
                            index + 1
                          )} from selection`}
                          title={`Remove ${getElementDisplayName(
                            obj,
                            index + 1
                          )} from selection`}
                        >
                          <svg
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2.25 14C1.8375 14 1.48438 13.8477 1.19062 13.5431C0.896875 13.2384 0.75 12.8722 0.75 12.4444V2.33333H0V0.777778H3.75V0H8.25V0.777778H12V2.33333H11.25V12.4444C11.25 12.8722 11.1031 13.2384 10.8094 13.5431C10.5156 13.8477 10.1625 14 9.75 14H2.25ZM3.75 10.8889H5.25V3.88889H3.75V10.8889ZM6.75 10.8889H8.25V3.88889H6.75V10.8889Z"
                              fill="#DC3545"
                            />
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Custom scrollbar for Selected section */}
              {selectedVScrollable > 0 && (
                <div
                  ref={selectedVTrackRef}
                  data-scrollbar-track="selected"
                  onMouseDown={handleSelectedVerticalTrackMouseDown}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: `${selectedScrollMetrics.clientHeight}px`,
                    width: 12,
                    zIndex: 999,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    background: "transparent",
                    cursor: "pointer",
                    pointerEvents: "auto",
                  }}
                >
                  <div
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleSelectedVerticalThumbMouseDown(e);
                    }}
                    style={{
                      position: "relative",
                      top: selectedVThumbTop,
                      width: 8,
                      height: selectedVThumbHeight,
                      borderRadius: 4,
                      background: "#9ca3af",
                      cursor: "grab",
                      transition:
                        dragState?.section === "selected" &&
                        dragState?.axis === "v"
                          ? "none"
                          : "background 0.15s ease, opacity 0.15s ease",
                      opacity:
                        dragState?.section === "selected" &&
                        dragState?.axis === "v"
                          ? 0.8
                          : 1,
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                      pointerEvents: "auto",
                    }}
                    onMouseEnter={(e) => {
                      if (
                        dragState?.section !== "selected" ||
                        dragState?.axis !== "v"
                      ) {
                        e.currentTarget.style.background = "#6B7280";
                        e.currentTarget.style.cursor = "grab";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (
                        dragState?.section !== "selected" ||
                        dragState?.axis !== "v"
                      ) {
                        e.currentTarget.style.background = "#9ca3af";
                        e.currentTarget.style.cursor = "grab";
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {!selectedSectionCollapsed &&
            effectiveSelectedObjects.length === 0 && (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#9CA3AF",
                  fontSize: "14px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                }}
              >
                No elements selected
              </div>
            )}
        </div>

        {/* Unselected Elements Section (only show when multi-select mode is enabled or when elements are selected via selection box) */}
        {(isMultiSelectMode ||
          individuallySelectedIds.size > 0 ||
          (selectedObjects && selectedObjects.length > 0)) &&
          (() => {
            const { unselected } = getAllElementsFromSameCanvasSeparated();
            if (unselected.length === 0) return null;

            return (
              <div style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => setUnselectedSectionCollapsed((prev) => !prev)}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#374151",
                    }}
                  >
                    Not Selected
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setUnselectedSectionCollapsed((prev) => !prev);
                    }}
                    aria-label={
                      unselectedSectionCollapsed
                        ? "Expand available elements"
                        : "Collapse available elements"
                    }
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <svg
                      width="14"
                      height="8"
                      viewBox="0 0 14 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        transition: "transform 0.2s ease",
                        transform: unselectedSectionCollapsed
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transformOrigin: "center",
                      }}
                    >
                      <path
                        d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                        fill="#6C757D"
                      />
                    </svg>
                  </button>
                </div>
                {!unselectedSectionCollapsed && (
                  <div
                    style={{
                      position: "relative",
                      height: unselectedVScrollable > 0 ? "300px" : "auto",
                      maxHeight: "300px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      ref={unselectedScrollAreaRef}
                      className="selection-scroll-container"
                      style={{
                        height: "100%",
                        maxHeight: "300px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "8px 8px 8px 8px",
                      }}
                    >
                      <div
                        style={{
                          paddingBottom:
                            unselectedVScrollable > 0 ? "12px" : "0px",
                        }}
                      >
                        {/* Unselected elements */}
                        {unselected.map((element, index) => (
                          <div
                            key={element.id}
                            style={{
                              padding: "8px",
                              marginBottom: "4px",
                              backgroundColor: "transparent",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "14px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                            onClick={() => handleAddElementToSelection(element)}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = "#F5F6F8";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }}
                          >
                            <span style={{ color: "#6B7280", flex: 1 }}>
                              {getElementDisplayName(element, index + 1)}
                            </span>
                            {/* Plus icon - at the end */}
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6.60603 11.6893L5.09724 11.6944L5.09212 6.58645L0.00183182 6.599L0.00695798 5.09021L5.10803 5.08844L5.09212 0.00505178L6.60091 -7.44536e-05L6.58835 5.09021L11.6963 5.09534L11.6912 6.60413L6.6078 6.58822L6.60603 11.6893Z"
                                  fill="#6C757D"
                                />
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Custom scrollbar for Not Selected section */}
                    {unselectedVScrollable > 0 && (
                      <div
                        ref={unselectedVTrackRef}
                        data-scrollbar-track="unselected"
                        onMouseDown={handleUnselectedVerticalTrackMouseDown}
                        style={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          height: `${unselectedScrollMetrics.clientHeight}px`,
                          width: 12,
                          zIndex: 999,
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          background: "transparent",
                          cursor: "pointer",
                          pointerEvents: "auto",
                        }}
                      >
                        <div
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            handleUnselectedVerticalThumbMouseDown(e);
                          }}
                          style={{
                            position: "relative",
                            top: unselectedVThumbTop,
                            width: 8,
                            height: unselectedVThumbHeight,
                            borderRadius: 4,
                            background: "#9ca3af",
                            cursor: "grab",
                            transition:
                              dragState?.section === "unselected" &&
                              dragState?.axis === "v"
                                ? "none"
                                : "background 0.15s ease, opacity 0.15s ease",
                            opacity:
                              dragState?.section === "unselected" &&
                              dragState?.axis === "v"
                                ? 0.8
                                : 1,
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                            pointerEvents: "auto",
                          }}
                          onMouseEnter={(e) => {
                            if (
                              dragState?.section !== "unselected" ||
                              dragState?.axis !== "v"
                            ) {
                              e.currentTarget.style.background = "#6B7280";
                              e.currentTarget.style.cursor = "grab";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (
                              dragState?.section !== "unselected" ||
                              dragState?.axis !== "v"
                            ) {
                              e.currentTarget.style.background = "#9ca3af";
                              e.currentTarget.style.cursor = "grab";
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
      </div>

      {/* Cancel and Delete buttons */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          marginTop: "20px",
        }}
      >
        <button
          onClick={handleCancel}
          style={{
            flex: 1,
            padding: "10px",
            backgroundColor: "#F3F4F6",
            color: "#374151",
            border: "1px solid #D1D5DB",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          style={{
            flex: 1,
            padding: "10px",
            backgroundColor: "#EF4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

SelectionEditor.propTypes = {
  selectedObjects: PropTypes.arrayOf(PropTypes.object),
  selectedObject: PropTypes.object,
  groupDragDisplay: PropTypes.shape({
    avgX: PropTypes.number,
    avgY: PropTypes.number,
    targetCanvas: PropTypes.string,
  }),
  setSelectedObjects: PropTypes.func,
  setSelectedObject: PropTypes.func,
  deleteSelectedObject: PropTypes.func,
  saveToHistory: PropTypes.func,
  onDuplicateToSelectedLocation: PropTypes.func,
  isMultiSelectMode: PropTypes.bool,
  setIsMultiSelectMode: PropTypes.func,
  individuallySelectedIds: PropTypes.instanceOf(Set),
  setIndividuallySelectedIds: PropTypes.func,
  updateShapeProperties: PropTypes.func,
  updateArrowProperties: PropTypes.func,
  updateDoubleSidedArrowProperties: PropTypes.func,
  updateLineProperties: PropTypes.func,
  updateTextProperties: PropTypes.func,
  updateImageProperties: PropTypes.func,
  handleCopy: PropTypes.func,
  handleCut: PropTypes.func,
  onToggleCloneMode: PropTypes.func,
  isCloneMode: PropTypes.bool,
  // Setter functions for deleting elements
  setRectangles: PropTypes.func,
  setCircles: PropTypes.func,
  setImages: PropTypes.func,
  setTexts: PropTypes.func,
  setArrows: PropTypes.func,
  setLines: PropTypes.func,
  setDoubleSidedArrows: PropTypes.func,
  setOffCanvasRectangles: PropTypes.func,
  setOffCanvasCircles: PropTypes.func,
  setOffCanvasImages: PropTypes.func,
  setOffCanvasTexts: PropTypes.func,
  setOffCanvasArrows: PropTypes.func,
  setOffCanvasLines: PropTypes.func,
  setOffCanvasDoubleSidedArrows: PropTypes.func,
  // Removed state setters for tracking deleted elements
  setRemovedRectangles: PropTypes.func,
  setRemovedCircles: PropTypes.func,
  setRemovedArrows: PropTypes.func,
  setRemovedDoubleSidedArrows: PropTypes.func,
  setRemovedScribbles: PropTypes.func,
  setRemovedTexts: PropTypes.func,
  setRemovedImages: PropTypes.func,
  setRemovedVideos: PropTypes.func,
  setRemovedLines: PropTypes.func,
  setRemovedOffCanvasRectangles: PropTypes.func,
  setRemovedOffCanvasCircles: PropTypes.func,
  setRemovedOffCanvasImages: PropTypes.func,
  setRemovedOffCanvasTexts: PropTypes.func,
  setRemovedOffCanvasArrows: PropTypes.func,
  setRemovedOffCanvasLines: PropTypes.func,
  setRemovedOffCanvasDoubleSidedArrows: PropTypes.func,
};

export default SelectionEditor;
